/* eslint-disable @typescript-eslint/no-unused-vars */

import { IMetricTelemetry } from '@microsoft/applicationinsights-common';
import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import {
  CLS_PERFORMANCE_CONFIG,
  computeLogNormalScore,
  FCP_PERFORMANCE_CONFIG,
  LCP_PERFORMANCE_CONFIG,
  TBT_PERFORMANCE_CONFIG,
  TTI_PERFORMANCE_CONFIG,
} from './performance-configs';
import { isMsx } from '../../utils/utils-msx';
import { store } from '../../ducks/store';

// private interface for Metric property on log
interface IMetricProperties {
  environment: string;
  hostContext: string;
  impersonatedUser?: string;
  pageName: string;
  metricName?: string;
  onFilterChange?: boolean; // true if measure is triggered by filter change
  value: number;
}

// Private interface to pass involved page and measure name to handlers
interface IMeasureMetaData {
  name: string; // measure name
  page: string; // page name ('global' if first loading time)
  startTime?: DOMHighResTimeStamp; // measure start time either Zero or TabChanged time
  onFilterChange?: boolean; // true if measure is triggered by filter change
}

export class PerformanceObserverService {
  // Instance of this class to pass around the app, so that we don't keep creating new instances/observers.
  private static instance: PerformanceObserverService;

  private environment: string | undefined;

  // Context of the host where the app is running. MSX vs Standalone
  private hostContext: string | undefined;

  // Performance API timeline observer
  private observer: PerformanceObserver | undefined;

  // Callback to be called everytime a metric is ready to be logged
  private logCallback: (metric: IMetricTelemetry, customProperties?: ICustomProperties) => void = () => undefined;

  // First Contentful Paint for the app.
  private globalFCP: DOMHighResTimeStamp | undefined;

  // Cumulative Layout Shift accumulator. Reset on every tab change.
  private CLSAccumulator: number = 0;

  // Total Blocking Time accumulator. Reset on every tab change.
  private TBTAccumulator: number = 0;

  // Entry names constants for performance API
  private readonly MARK_ENTRY_TYPE = 'mark';

  private readonly MEASURE_ENTRY_TYPE = 'measure';

  private readonly LONG_TASK_ENTRY_TYPE = 'longtask';

  private readonly LAYOUT_SHIFT_ENTRY_TYPE = 'layout-shift';

  // Mark Names
  private TIME_ZERO_MARK_NAME = 'time-zero';

  private FCP_MARK_NAME = 'fcp-mark';

  private FCP_MEASURE_NAME = 'fcp';

  private LCP_MARK_NAME = 'lcp-mark';

  private LCP_MEASURE_NAME = 'lcp';

  private TTI_MARK_NAME = 'tti-mark';

  private TTI_MEASURE_NAME = 'tti';

  private FMP_MARK_NAME = 'fmp-mark';

  private FMP_MEASURE_NAME = 'fmp';

  public static get Instance() {
    if (!this.instance) {
      this.instance = new this();
    }
    return this.instance;
  }

  public reStart() {
    this.initPerformanceObserver();
    this.setFirstContentfulPaintMark();
    this.setLargestContentfulPaintMark();
    this.setTimeToInteractiveMark();
    const currentPage = store.getState().filters.activeTab;
    this.setFirstMeaningfulPaintMark(currentPage || '');
  }

  public init(
    prefix: string = 'webper',
    env: string = '',
    logCallback: (metric: IMetricTelemetry, customProperties?: ICustomProperties) => void = () => undefined
  ) {
    // Set environment to add on logs
    this.environment = env;
    // Set callback to be called everytime a metric is ready to be logged
    this.logCallback = logCallback;
    // Override marks and measure names with prefix
    this.TIME_ZERO_MARK_NAME = `${prefix}-time-zero`;
    this.FCP_MARK_NAME = `${prefix}-fcp-mark`;
    this.FCP_MEASURE_NAME = `${prefix}-fcp`;
    this.LCP_MARK_NAME = `${prefix}-lcp-mark`;
    this.LCP_MEASURE_NAME = `${prefix}-lcp`;
    this.TTI_MARK_NAME = `${prefix}-tti-mark`;
    this.TTI_MEASURE_NAME = `${prefix}-tti`;
    this.FMP_MARK_NAME = `${prefix}-fmp-mark`;
    this.FMP_MEASURE_NAME = `${prefix}-fmp`;
    this.initPerformanceObserver();

    // Set Host Context
    if (isMsx()) {
      this.hostContext = 'MSX';
    } else {
      this.hostContext = 'Standalone';
    }

    // Start looking at Page Visibility API to stop measuring when the page is unfocused.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.cleanup();
      }
    });
  }

  public cleanup() {
    if (this.observer) {
      // clear mem performance marks and measures
      performance.clearMarks(this.TIME_ZERO_MARK_NAME);
      performance.clearMarks(this.FCP_MARK_NAME);
      performance.clearMarks(this.LCP_MARK_NAME);
      performance.clearMarks(this.FMP_MARK_NAME);
      performance.clearMarks(this.TTI_MARK_NAME);

      performance.clearMeasures(this.LCP_MEASURE_NAME);
      performance.clearMeasures(this.FCP_MEASURE_NAME);
      performance.clearMeasures(this.FMP_MEASURE_NAME);
      performance.clearMeasures(this.TTI_MEASURE_NAME);
      this.observer.disconnect();
    }
  }

  private initPerformanceObserver() {
    this.observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        switch (entry.entryType) {
          case this.MARK_ENTRY_TYPE:
            this.marksHandler(entry as PerformanceMark);
            break;
          case this.MEASURE_ENTRY_TYPE:
            this.measuresHandler(entry as PerformanceMeasure);
            break;
          case this.LONG_TASK_ENTRY_TYPE:
            this.longTasksHandler(entry); // No interface is exposed for long tasks
            break;
          case this.LAYOUT_SHIFT_ENTRY_TYPE:
            this.layoutShiftsHandler(entry); // No interface is exposed for layout shifts
            break;
          default:
            break;
        }
      });
    });
    this.observer.observe({
      entryTypes: [this.MEASURE_ENTRY_TYPE, this.MARK_ENTRY_TYPE, this.LONG_TASK_ENTRY_TYPE, this.LAYOUT_SHIFT_ENTRY_TYPE],
    });

    // Give a reference at the window object so the MSX shell can clean the observer
    if (window['msx-mem-seller-shell' as any]) {
      (window['msx-mem-seller-shell' as any] as any).performanceObserver = this;
    }
  }

  private marksHandler(entry: PerformanceMark) {
    // HANDLING MARKS
    switch (entry.name) {
      case this.FCP_MARK_NAME:
        this.calculateFirstContentfulPaint();
        break;
      case this.LCP_MARK_NAME:
        this.calculateLargestContentfulPaint();
        break;
      case this.TTI_MARK_NAME:
        this.calculateTimeToInteractive();
        break;
      case this.FMP_MARK_NAME:
        this.calculateFirstMeaningfulPaint();
        this.calculateMetrics();
        break;
      default:
        break;
    }
  }

  private measuresHandler(entry: PerformanceMeasure) {
    // HANDLING MEASURES
    switch (entry.name) {
      case this.FCP_MEASURE_NAME:
        this.logPerformanceMeasure(entry);
        break;
      case this.LCP_MEASURE_NAME:
        this.logPerformanceMeasure(entry);
        break;
      case this.FMP_MEASURE_NAME:
        this.logPerformanceMeasure(entry);
        this.cleanup();
        break;
      case this.TTI_MEASURE_NAME:
        this.logPerformanceMeasure(entry);
        break;
      default:
        break;
    }
  }

  private layoutShiftsHandler(entry: any) {
    if (!entry.hadRecentInput) {
      this.CLSAccumulator += entry.value;
    }
  }

  private longTasksHandler(entry: PerformanceEntry) {
    // We only care about long tasks after global FCP that are longer than 50ms.
    if (this.globalFCP && entry.duration > 50) {
      this.TBTAccumulator += entry.duration - 50;
    }
  }

  // Set the First Contentful Paint mark
  public setFirstContentfulPaintMark() {
    performance.mark(this.FCP_MARK_NAME);
  }

  // Calculate FCP
  private calculateFirstContentfulPaint() {
    let fcpMeasure;
    const metadata: IMeasureMetaData = {
      name: 'FCP',
      page: 'global',
    };

    // Get Time Zero if available (on MSX there is timeZero Mark, on standalone there is not)
    const timeZeroMarks = performance.getEntriesByName(this.TIME_ZERO_MARK_NAME);

    // Get the FCP Mark
    const FCPMarks = performance.getEntriesByName(this.FCP_MARK_NAME);
    const FCP = FCPMarks[FCPMarks.length - 1];

    if (timeZeroMarks.length > 0) {
      const timeZeroMark = timeZeroMarks[timeZeroMarks.length - 1];
      fcpMeasure = performance.measure(this.FCP_MEASURE_NAME, {
        detail: metadata,
        start: timeZeroMark.startTime,
        end: FCP.startTime,
      });
    } else {
      fcpMeasure = performance.measure(this.FCP_MEASURE_NAME, {
        detail: metadata,
        end: FCP.startTime,
      });
    }
    this.updateGlobalFCP(fcpMeasure.startTime + fcpMeasure.duration);
  }

  // Set the Largest ContentFul Paint mark
  public setLargestContentfulPaintMark() {
    performance.mark(this.LCP_MARK_NAME);
  }

  // Calculate LCP
  private calculateLargestContentfulPaint() {
    let lcpMeasure;
    const metadata: IMeasureMetaData = {
      name: 'LCP',
      page: 'global',
    };

    // Get Time Zero if available (on MSX there is timeZero Mark, on standalone there is not)
    const timeZeroMarks = performance.getEntriesByName(this.TIME_ZERO_MARK_NAME);

    // Get the LCP Mark
    const LCPMarks = performance.getEntriesByName(this.LCP_MARK_NAME);
    const LCP = LCPMarks[LCPMarks.length - 1];

    if (timeZeroMarks.length > 0) {
      const timeZeroMark = timeZeroMarks[timeZeroMarks.length - 1];
      lcpMeasure = performance.measure(this.LCP_MEASURE_NAME, {
        detail: metadata,
        start: timeZeroMark.startTime,
        end: LCP.startTime,
      });
    } else {
      lcpMeasure = performance.measure(this.LCP_MEASURE_NAME, {
        detail: metadata,
        end: LCP.startTime,
      });
    }
  }

  // Set the Time to Interactive mark
  public setTimeToInteractiveMark() {
    performance.mark(this.TTI_MARK_NAME);
  }

  // Set the First Meaningful Paint mark
  public setFirstMeaningfulPaintMark(pageName: string) {
    performance.mark(this.FMP_MARK_NAME, { detail: pageName });
  }

  // Calculate FMP
  private calculateFirstMeaningfulPaint() {
    let fmpMeasure;
    const metadata: IMeasureMetaData = {
      name: 'FMP',
      page: '',
    };

    // Get Time Zero if available (on MSX there is timeZero Mark, on standalone there is not)
    const timeZeroMarks = performance.getEntriesByName(this.TIME_ZERO_MARK_NAME);

    // Get the LCP Mark
    const FMPMarks = performance.getEntriesByName(this.FMP_MARK_NAME) as PerformanceMark[];
    const FMP = FMPMarks[FMPMarks.length - 1];
    if (FMP) {
      metadata.page = FMP.detail;
    }
    if (timeZeroMarks.length > 0) {
      const timeZeroMark = timeZeroMarks[timeZeroMarks.length - 1];
      fmpMeasure = performance.measure(this.FMP_MEASURE_NAME, {
        detail: metadata,
        start: timeZeroMark.startTime,
        end: FMP.startTime,
      });
    } else {
      fmpMeasure = performance.measure(this.FMP_MEASURE_NAME, {
        detail: metadata,
        end: FMP.startTime,
      });
    }
  }

  // Calculate TTI
  private calculateTimeToInteractive() {
    let ttiMeasure;
    const metadata: IMeasureMetaData = {
      name: 'TTI',
      page: 'global',
    };

    // Get Time Zero for TTI is the latest FCP mark
    const timeZeroMarks = performance.getEntriesByName(this.TIME_ZERO_MARK_NAME);

    // Get the TTI Mark
    const TTIMarks = performance.getEntriesByName(this.TTI_MARK_NAME);
    const TTI = TTIMarks[TTIMarks.length - 1];

    if (timeZeroMarks.length > 0) {
      const timeZeroMark = timeZeroMarks[timeZeroMarks.length - 1];
      ttiMeasure = performance.measure(this.TTI_MEASURE_NAME, {
        detail: metadata,
        start: timeZeroMark.startTime,
        end: TTI.startTime,
      });
    } else {
      ttiMeasure = performance.measure(this.TTI_MEASURE_NAME, {
        detail: metadata,
        end: TTI.startTime,
      });
    }
  }

  // Updates the Global FCP property to log it. ONLY if there was not an FCP before.
  private updateGlobalFCP(timestamp: DOMHighResTimeStamp) {
    if (!this.globalFCP) {
      this.globalFCP = timestamp;
    }
  }

  private calculateMetrics() {
    this.reportCLS();
    // We keep this here in case we need to do Lab research to improve performance and TBT
    // It is not recommended to measure TBT on the field as the numbers you get will be useless
    // for reference go to => https://web.dev/tbt/
    this.reportTBT();
  }

  // Calculate Cummulative Layout Shift score for a period of time.
  private reportCLS() {
    this.logPerformanceMetric('global', 'CLS', this.CLSAccumulator);
  }

  // Calculate Total Blocking Time score for a period of time.
  private reportTBT() {
    this.logPerformanceMetric('global', 'TBT', this.TBTAccumulator);
  }

  private logPerformanceMeasure(entry: PerformanceMeasure) {
    this.logPerformanceMetric(entry.detail.page, entry.detail.name, entry.duration, entry.detail.onFilterChange);
  }

  private logPerformanceMetric(pageName: string, metricName: string, value: number, onFilterChange: boolean = false) {
    // Pending to Get Impersonated User
    const properties: IMetricProperties = {
      environment: `${this.environment}`,
      hostContext: `${this.hostContext}`,
      pageName,
      value,
      onFilterChange,
      metricName,
    };
    this.logCallback({ name: `${pageName}-${metricName}`, average: value }, properties);

    // Calculate LH score and log it.
    let lhScore = 0;
    switch (metricName) {
      case 'FCP':
        lhScore = computeLogNormalScore(FCP_PERFORMANCE_CONFIG, value);
        break;
      case 'LCP':
        lhScore = computeLogNormalScore(LCP_PERFORMANCE_CONFIG, value);
        break;
      case 'TTI':
        lhScore = computeLogNormalScore(TTI_PERFORMANCE_CONFIG, value);
        break;
      case 'TBT':
        lhScore = computeLogNormalScore(TBT_PERFORMANCE_CONFIG, value);
        break;
      case 'CLS':
        lhScore = computeLogNormalScore(CLS_PERFORMANCE_CONFIG, value);
        break;
      default:
        break;
    }

    properties.value = lhScore;
    this.logCallback({ name: `${pageName}-${metricName}-LH`, average: lhScore }, properties);
  }
}

export default PerformanceObserverService.Instance;
